'use client';

import { cn } from '@fshr/shared-react';
import { IconChevronLightRight, IconChevronLightLeft } from '../icon';
import { useCallback } from 'react';
import Button from '../button';

interface CarouselButtonsProps {
	childIndex: number;
	minChildIndex: number;
	maxChildIndex: number;

	// eslint-disable-next-line no-unused-vars
	setChildIndex: (fn: (n: number) => number) => void;
	incrementAmount?: number;
	labels: {
		previous: string;
		next: string;
	};
	/** Extra classes for the arrow bar, e.g. to inset it to the content column when the track bleeds. */
	className?: string;
	mediaHeight?: number;
}

export default function CarouselButtons({
	childIndex,
	minChildIndex,
	maxChildIndex,
	setChildIndex,
	incrementAmount = 1,
	labels,
	className,
}: CarouselButtonsProps) {
	const increment = useCallback((n: number) => n + incrementAmount, [incrementAmount]);
	const decrement = useCallback((n: number) => n - incrementAmount, [incrementAmount]);
	return (
		<div
			className={cn([
				'hidden',
				'md:flex',
				'absolute',
				'top-1/2',
				'left-0',
				'right-0',
				'h-0',
				'flex-row',
				'justify-between',
				className,
			])}
		>
			<div className="w-300 -ml-300 flex justify-center items-center">
				{childIndex > minChildIndex && (
					<button
						type="button"
						onClick={() => setChildIndex(decrement)}
						className="w-200 h-200 relative z-10"
						aria-label={labels.previous}
					>
						<IconChevronLightLeft />
					</button>
				)}
			</div>

			<div className="w-300 -mr-300 flex justify-center items-center">
				{childIndex < maxChildIndex && (
					<button
						type="button"
						onClick={() => setChildIndex(increment)}
						className="w-200 h-200 relative z-10"
						aria-label={labels.next}
					>
						<IconChevronLightRight />
					</button>
				)}
			</div>
		</div>
	);
}

export const RoundedButtons = (props: CarouselButtonsProps) => {
	const {
		childIndex,
		minChildIndex,
		maxChildIndex,
		setChildIndex,
		incrementAmount = 1,
		labels,
		className,
		mediaHeight = 0,
	} = props;

	const increment = useCallback((n: number) => n + incrementAmount, [incrementAmount]);
	const decrement = useCallback((n: number) => n - incrementAmount, [incrementAmount]);

	return (
		<div
			className={cn(
				'absolute left-0 right-0 hidden md:flex items-center justify-between pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300',
				className,
				mediaHeight > 0 ? 'top-0' : 'inset-y-0'
			)}
			style={mediaHeight > 0 ? { height: mediaHeight } : undefined}
		>
			<div
				className="relative ml-inline-xs pointer-events-auto"
				onClick={e => e.stopPropagation()}
				style={{ visibility: childIndex > minChildIndex ? 'visible' : 'hidden' }}
			>
				<button
					type="button"
					onClick={e => {
						e.preventDefault();
						e.stopPropagation();
						setChildIndex(decrement);
					}}
					className="cursor-pointer w-500 h-500 rounded-full bg-white hover:bg-button-on-image-primary-hover shadow-[0px_5px_7.5px_rgba(3,3,3,0.12)] flex items-center justify-center pointer-events-auto transition-all duration-300 hover:scale-110"
					aria-label={labels.previous}
				>
					<IconChevronLightLeft width={20} height={20} />
				</button>
			</div>
			<div
				className="relative mr-inline-xs pointer-events-auto"
				onClick={e => e.stopPropagation()}
				style={{ visibility: childIndex < maxChildIndex ? 'visible' : 'hidden' }}
			>
				<Button
					intent="unstyled"
					type="button"
					onClick={e => {
						e.preventDefault();
						e.stopPropagation();
						setChildIndex(increment);
					}}
					className="cursor-pointer w-500 h-500 rounded-full bg-white hover:bg-button-on-image-primary-hover shadow-[0px_5px_7.5px_rgba(3,3,3,0.12)] flex items-center justify-center pointer-events-auto transition-all duration-300 hover:scale-110"
					aria-label={labels.next}
				>
					<IconChevronLightRight width={20} height={20} />
				</Button>
			</div>
		</div>
	);
};
